/**
 * A coluna da direita: quem está aqui.
 *
 * Agrupada como as pessoas esperam — roles destacadas primeiro, depois o resto
 * online, os offline no fim e a cinzento. O agrupamento em si vive no selector
 * (`memberGroups`), aqui só se desenha.
 */

import type { Member, PublicUser } from "@greenvoice/shared";
import { useState } from "react";
import { useVoiceSnapshot, voiceLive } from "../hooks/useVoice";
import { useIsMobile } from "../lib/media";
import { isOnline, memberColor, memberGroups, ownVoiceMarks } from "../store/selectors";
import { useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconChevron, IconHeadphonesOff, IconMoon } from "./Icons";
import { UserCard } from "./UserCard";

/**
 * A seta que arruma esta coluna — a GÉMEA da que arruma os canais, virada ao
 * contrário.
 *
 * *"remove do header e coloca o botão > igual tá no outro lado... assim deixa
 * padronizado"*. E é mais do que arrumação: cada seta aponta para onde manda a
 * sua coluna, portanto uma aponta para a esquerda e a outra para a direita.
 * Duas colunas, duas setas, a mesma regra — em vez de um botão numa barra
 * partilhada, que não dizia de qual das colunas estava a falar.
 *
 * Num ecrã estreito não existe: ali esta coluna é uma GAVETA, e uma gaveta abre
 * pelo botão do cabeçalho e fecha pelo véu (ver ChatView).
 */
function MembersCollapse({ collapsed }: { collapsed: boolean }) {
  const toggle = useStore((s) => s.toggleMemberList);
  const mobile = useIsMobile();
  const label = collapsed ? "Mostrar os membros" : "Esconder os membros";

  if (mobile) return null;
  return (
    <button
      type="button"
      className="icon-button members__collapse"
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
    >
      <IconChevron size={16} />
    </button>
  );
}

export function MemberList() {
  const state = useStore();
  const mobile = useIsMobile();
  const serverId = state.activeServerId;
  const channel = state.activeChannelId ? state.channels[state.activeChannelId] : undefined;

  // Arrumada: no computador sobra a TIRA com a seta — a pega não pode
  // desaparecer com o que arrumou, senão não há como a trazer de volta (a mesma
  // lição da coluna dos canais). Num ecrã estreito é uma gaveta fechada, e uma
  // gaveta fechada não deixa tira nenhuma: quem a abre é o botão do cabeçalho.
  if (!state.memberListOpen) {
    if (mobile) return null;
    return (
      <aside className="members members--mini">
        <div className="members__head">
          <MembersCollapse collapsed />
        </div>
      </aside>
    );
  }

  // Numa conversa directa não há membros de servidor — mostram-se os presentes.
  if (!serverId) {
    if (!channel || (channel.type !== "DM" && channel.type !== "GROUP_DM")) return null;
    const people = channel.recipientIds.map((id) => state.users[id]).filter((u): u is PublicUser => Boolean(u));

    return (
      <aside className="members">
        <div className="members__head">
          <MembersCollapse collapsed={false} />
        </div>
        <h3 className="members__label">Nesta conversa — {people.length}</h3>
        {people.map((user) => (
          <UserRow key={user.id} user={user} color={null} note={null} />
        ))}
      </aside>
    );
  }

  const groups = memberGroups(state, serverId);

  return (
    <aside className="members">
      <div className="members__head">
        <MembersCollapse collapsed={false} />
      </div>
      {groups.map((group) => (
        <section key={group.role?.id ?? group.label} className="members__group">
          <h3 className="members__label">
            {group.label} — {group.members.length}
          </h3>
          {group.members.map((member) => (
            <MemberRow key={member.user.id} member={member} />
          ))}
        </section>
      ))}

      {groups.length === 0 ? <p className="members__empty">Ainda não há ninguém aqui.</p> : null}
    </aside>
  );
}

function MemberRow({ member }: { member: Member }) {
  const state = useStore();
  const color = memberColor(state, member);
  const user = member.user;
  const name = member.nickname ?? user.displayName ?? user.username;

  return (
    <UserRow user={user} color={color} note={user.statusText ?? null} displayName={name} dimmed={!isOnline(user)} />
  );
}

interface UserRowProps {
  user: PublicUser;
  color: string | null;
  note: string | null;
  displayName?: string;
  dimmed?: boolean;
}

function UserRow({ user, color, note, displayName, dimmed = false }: UserRowProps) {
  // O elemento e não um booleano: o cartão precisa dele para se posicionar e
  // para saber que um clique NELE não conta como "clique fora".
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const name = displayName ?? user.displayName ?? user.username;

  return (
    <div className={`member${dimmed ? " is-offline" : ""}`}>
      <button
        type="button"
        className="member__button"
        onClick={(event) => {
          const target = event.currentTarget;
          setAnchor((current) => (current ? null : target));
        }}
      >
        <Avatar user={user} size={32} showStatus />
        <span className="member__names">
          <span className="member__line">
            {/* À FRENTE do nome: um nome comprido corta-se com reticências, e um
                sinal pendurado depois dele era o primeiro a desaparecer — logo
                na pessoa cujo nome não cabe, que é quando faz mais falta. */}
            <MemberMarks user={user} />
            <strong style={color ? { color } : undefined}>{name}</strong>
          </span>
          {note ? <small>{note}</small> : null}
        </span>
      </button>

      {anchor ? (
        <UserCard
          user={user}
          anchor={anchor}
          color={color}
          displayName={displayName ?? null}
          onClose={() => setAnchor(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Os sinais ao lado do nome: auscultadores cortados e ausente.
 *
 * Respondem à pergunta que se faz ANTES de falar com alguém — "esta pessoa
 * ouve-me?" — e é por isso que vivem aqui, no sítio onde se escolhe com quem
 * falar, e não só dentro da sala de voz. O ponto de estado do avatar diz que a
 * pessoa está IDLE mas não diz que está sem som; um utilizador não tem como
 * adivinhar que um ponto amarelo significa "não vai responder já".
 *
 * O MICROFONE não está nesta lista, e é de propósito: «esse microfone do nome,
 * no greenvoice, não precisa, apenas no office 2d». Continua a ser desenhado no
 * escritório 2D (`marksFor`, em `officeAvatars`) — lá o boneco não tem mais
 * nada a dizer por si, e é lá que se anda a decidir de quem nos aproximamos. Na
 * lista de membros o microfone fechado é o estado mais comum que há: marcava
 * meia lista e deixava de distinguir seja o que for.
 *
 * A fonte continua a ser a MESMA do escritório 2D (`voiceStates` +
 * `users[id].status`), para o mapa e a lista nunca discordarem — incluindo a
 * regra de que o MEU estado se lê no motor local e não no eco do servidor
 * (`ownVoiceMarks`), sem a qual eu era o único da lista a nunca aparecer surdo.
 */
function MemberMarks({ user }: { user: PublicUser }) {
  const echo = useStore((s) => s.voiceStates[user.id]);
  const isSelf = useStore((s) => s.self?.id === user.id);
  const snapshot = useVoiceSnapshot();

  const voice = isSelf
    ? ownVoiceMarks(echo, {
        connected: voiceLive(snapshot),
        micEnabled: snapshot.micEnabled,
        deafened: snapshot.deafened,
      })
    : echo;
  const deafened = Boolean(voice && (voice.selfDeaf || voice.serverDeaf));
  const away = user.status === "IDLE";
  if (!deafened && !away) return null;

  // Os glifos são `aria-hidden` (decoração): o significado tem de existir em
  // TEXTO, senão um leitor de ecrã lê o nome e mais nada — que é exactamente o
  // caso em que a informação faz falta.
  const said = [deafened ? "sem som" : null, away ? "ausente" : null].filter(Boolean).join(", ");

  // A ausência é uma LUA, não a palavra "Ausente": a etiqueta de texto gritava
  // mais alto do que o nome ao lado dela, roubava a largura de que o nome
  // precisava, e ficava a discordar do mapa 2D — que já desenha uma lua para
  // dizer o mesmo. Três sinais do mesmo tamanho lêem-se como um conjunto; dois
  // ícones e uma palavra lêem-se como um erro de desenho.
  return (
    <span className="member__marks" title={said}>
      {deafened ? <IconHeadphonesOff size={13} /> : null}
      {away ? <IconMoon size={12} className="member__away" /> : null}
      <span className="sr-only">{said}</span>
    </span>
  );
}
